import React from 'react';
import { Star, Heart, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { RatingStats } from '@/types/reviews';

interface DisplayHeaderProps {
  name: string;
  displayType: string | null;
  ratingStats?: RatingStats | null;
  holidays: string[];
  location: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  rightContent?: React.ReactNode;
}

const DisplayHeader: React.FC<DisplayHeaderProps> = ({
  name,
  displayType,
  ratingStats,
  holidays,
  location,
  isFavorite,
  onToggleFavorite,
  rightContent
}) => {
  // Helper to render star rating
  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={16}
        className={cn(
          i < Math.round(rating) 
            ? "fill-amber-500 text-amber-500" 
            : "text-gray-300"
        )}
      />
    ));
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-3xl font-bold">{name}</h1>
          <div className="flex items-center mt-2 space-x-3">
            {displayType && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                {displayType}
              </Badge>
            )}
            
            {/* Rating display */}
            {ratingStats && ratingStats.rating_count > 0 ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  {renderStars(ratingStats.average_rating)}
                </div>
                <span className="text-amber-600 font-medium">
                  {ratingStats.average_rating.toFixed(1)}
                </span>
                <span className="text-gray-500 text-sm">
                  ({ratingStats.rating_count} {ratingStats.rating_count === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            ) : (
              <div className="flex items-center text-gray-400">
                <Star size={16} className="mr-1" />
                <span className="text-sm">No reviews yet</span>
              </div>
            )}
            
            {holidays && holidays.length > 0 && holidays.map((holiday) => (
              <Badge key={holiday} variant="outline">
                {holiday}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {rightContent}
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={onToggleFavorite}
          >
            <Heart
              size={18}
              className={cn(
                "transition-colors",
                isFavorite ? "fill-destructive stroke-destructive" : "fill-none"
              )}
            />
          </Button>
        </div>
      </div>
      
      <div className="flex items-center mb-4">
        <MapPin size={16} className="text-muted-foreground mr-2" />
        <span>{location}</span>
      </div>
    </div>
  );
};

export default DisplayHeader;
